/**
 * Pure tiered-bulk pricing maths. Shared by the Pack Engine pricing registry,
 * the storefront corporate pages and server-side quote pricing so all three
 * always agree.
 */
import type { PackPriceTier } from "./types";

export type TierLike = {
  minQty: number;
  maxQty?: number | null;
  unitPriceCents?: number | null;
  discountPercent?: number | null;
  label?: string | null;
};

function round(n: number): number {
  return Math.round(n);
}

export function activeTiers(tiers: TierLike[]): TierLike[] {
  return [...tiers].sort((a, b) => a.minQty - b.minQty);
}

/** The tier that applies to `qty`, or null when below the first tier. */
export function resolveTier(tiers: TierLike[], qty: number): TierLike | null {
  let match: TierLike | null = null;
  for (const t of activeTiers(tiers)) {
    const withinMin = qty >= t.minQty;
    const withinMax = t.maxQty == null || qty <= t.maxQty;
    if (withinMin && withinMax) match = t;
    else if (withinMin && t.maxQty != null && qty > t.maxQty) continue;
  }
  return match;
}

export type TieredPrice = {
  qty: number;
  baseUnitCents: number;
  unitPriceCents: number;
  subtotalCents: number;
  discountCents: number;
  totalCents: number;
  tierLabel: string | null;
};

/** Applies the matching tier to a base unit price (paise). */
export function priceTiered(baseUnitCents: number, qty: number, tiers: TierLike[]): TieredPrice {
  const safeQty = Math.max(0, Math.floor(qty));
  const subtotal = round(baseUnitCents * safeQty);
  const tier = resolveTier(tiers, safeQty);
  let unit = baseUnitCents;
  if (tier) {
    if (typeof tier.unitPriceCents === "number" && tier.unitPriceCents > 0) {
      unit = tier.unitPriceCents;
    } else if (typeof tier.discountPercent === "number" && tier.discountPercent > 0) {
      unit = round(baseUnitCents * (1 - Math.min(100, tier.discountPercent) / 100));
    }
  }
  const total = round(unit * safeQty);
  return {
    qty: safeQty,
    baseUnitCents,
    unitPriceCents: unit,
    subtotalCents: subtotal,
    discountCents: Math.max(0, subtotal - total),
    totalCents: total,
    tierLabel: tier?.label ?? (tier ? `${tier.minQty}+` : null),
  };
}

export function tierFromRow(t: PackPriceTier): TierLike {
  return {
    minQty: t.minQty,
    maxQty: t.maxQty,
    unitPriceCents: t.unitPriceCents,
    discountPercent: t.discountPercent,
    label: t.label,
  };
}

export type MoqIssue = { code: "below_min_qty" | "above_max_qty"; message: string };

/** Minimum / maximum order quantity validation for corporate packs. */
export function validateOrderQty(
  qty: number,
  opts: { minOrderQty: number; maxOrderQty?: number | null },
): MoqIssue[] {
  const issues: MoqIssue[] = [];
  if (!Number.isFinite(qty) || qty < opts.minOrderQty) {
    issues.push({
      code: "below_min_qty",
      message: `Minimum order quantity for this pack is ${opts.minOrderQty}.`,
    });
  }
  if (opts.maxOrderQty != null && qty > opts.maxOrderQty) {
    issues.push({
      code: "above_max_qty",
      message: `Maximum order quantity for this pack is ${opts.maxOrderQty}.`,
    });
  }
  return issues;
}
